// src/components/business/BusinessSettingsTab.tsx
//
// Aba "Configurações" da tela do negócio — réplica literal do wireframe
// seção 04 (#bp-cfg): [Nome · Tipo] [Subtipo · Status] [Mercado (DNA) ·
// Modelo de receita] + Público (DNA) + footer Cancelar/Salvar alterações.
// SEM C-Levels (decisão Breno). Classes .wf-nc-2col/.wf-field em wf-biz.css
// e wireframe.css.
//
// Salvar roteia cada campo pro endpoint certo (C3):
//   nome/tipo/subtipo  → PATCH /v1/businesses/:id        (api.updateBusiness)
//   mercado/público/modelo de receita → PATCH /:id/dna   (api.updateBusinessDNA)
//   status             → PATCH /:id/lifecycle            (api.transitionBusinessLifecycle)
//
// Modelo de receita ("dna + colunas"): leitura dna.revenue_model com fallback
// na coluna businesses.revenue_goal; escrita em dna.revenue_model.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  type Business,
  type BusinessDNA,
  type BusinessType,
  type LifecycleStatus,
} from '@/lib/api';
import { WfButton } from '@/components/wf';
import { BusinessDeleteDialog } from './BusinessDeleteDialog';
import { BusinessLogo, BusinessLogoUploadButton } from './BusinessLogo';
import {
  LIFECYCLE_LABELS,
  LIFECYCLE_TRANSITIONS,
  REVENUE_GOAL_OPTIONS,
  TYPE_OPTIONS,
} from './business-constants';

interface BusinessSettingsTabProps {
  business: Business;
  dna: BusinessDNA | null;
  onBusinessUpdated: (biz: Business) => void;
  onDnaUpdated: (dna: BusinessDNA) => void;
}

export function BusinessSettingsTab({
  business,
  dna,
  onBusinessUpdated,
  onDnaUpdated,
}: BusinessSettingsTabProps) {
  const [name, setName] = useState(business.name);
  const [type, setType] = useState<string>(business.type);
  const [subtype, setSubtype] = useState(business.subtype ?? '');
  const [status, setStatus] = useState<string>(business.lifecycle_status);
  const [market, setMarket] = useState(dna?.market ?? '');
  // "dna + colunas": dna.revenue_model manda; coluna revenue_goal é o fallback.
  const [revenueModel, setRevenueModel] = useState(
    dna?.revenue_model ?? business.revenue_goal ?? '',
  );
  const [audience, setAudience] = useState(dna?.audience ?? '');

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const navigate = useNavigate();

  // O wireframe mostra os 5 status livres no select, mas o lifecycle do
  // backend é uma state machine — só oferecemos o atual + transições válidas.
  const statusOptions = [
    business.lifecycle_status,
    ...(LIFECYCLE_TRANSITIONS[business.lifecycle_status] ?? []),
  ];

  const resetForm = () => {
    setName(business.name);
    setType(business.type);
    setSubtype(business.subtype ?? '');
    setStatus(business.lifecycle_status);
    setMarket(dna?.market ?? '');
    setRevenueModel(dna?.revenue_model ?? business.revenue_goal ?? '');
    setAudience(dna?.audience ?? '');
    setMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      let updatedBiz = business;

      // 1) Campos básicos → PATCH /v1/businesses/:id
      const bizChanged =
        name.trim() !== business.name ||
        type !== business.type ||
        (subtype.trim() || null) !== (business.subtype ?? null);
      if (bizChanged) {
        updatedBiz = await api.updateBusiness(business.id, {
          name: name.trim(),
          type: type as BusinessType,
          subtype: subtype.trim() || null,
        });
      }

      // 2) DNA → PATCH /v1/businesses/:id/dna
      const dnaChanged =
        (market.trim() || null) !== (dna?.market ?? null) ||
        (revenueModel || null) !== (dna?.revenue_model ?? business.revenue_goal ?? null) ||
        (audience.trim() || null) !== (dna?.audience ?? null);
      if (dnaChanged) {
        const updatedDna = await api.updateBusinessDNA(business.id, {
          market: market.trim() || null,
          revenue_model: revenueModel || null,
          audience: audience.trim() || null,
        });
        onDnaUpdated(updatedDna);
      }

      // 3) Status → PATCH /v1/businesses/:id/lifecycle
      if (status !== business.lifecycle_status) {
        updatedBiz = await api.transitionBusinessLifecycle(
          business.id,
          status as LifecycleStatus,
        );
      }

      if (bizChanged || status !== business.lifecycle_status) {
        onBusinessUpdated(updatedBiz);
      }

      setMsg('Alterações salvas com sucesso');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wf-bizpanel">
      {/* BIZ-C-02 — logo editável também NA ABA Configurações (além do header). */}
      <div className="wf-field" style={{ marginBottom: 14 }}>
        <span className="wf-label">Logo</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BusinessLogo business={business} />
          <BusinessLogoUploadButton
            business={business}
            onUpdated={onBusinessUpdated}
            onError={(m) => setMsg(m)}
          />
        </div>
      </div>
      <div className="wf-nc-2col">
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-name">Nome do negócio</label>
          <input
            id="cfg-name"
            className="wf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-type">Tipo</label>
          <select
            id="cfg-type"
            className="wf-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wf-nc-2col">
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-subtype">Subtipo</label>
          <input
            id="cfg-subtype"
            className="wf-input"
            value={subtype}
            onChange={(e) => setSubtype(e.target.value)}
            placeholder="ex: saas, marketplace"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-status">Status</label>
          <select
            id="cfg-status"
            className="wf-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>{LIFECYCLE_LABELS[s] ?? s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wf-nc-2col">
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-market">Mercado (DNA)</label>
          <input
            id="cfg-market"
            className="wf-input"
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            placeholder="mercado-alvo"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="cfg-revenue">Modelo de receita</label>
          <select
            id="cfg-revenue"
            className="wf-select"
            value={revenueModel}
            onChange={(e) => setRevenueModel(e.target.value)}
          >
            {!REVENUE_GOAL_OPTIONS.includes(revenueModel as never) && (
              <option value={revenueModel}>{revenueModel || '—'}</option>
            )}
            {REVENUE_GOAL_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wf-field">
        <label className="wf-label" htmlFor="cfg-audience">Público (DNA)</label>
        <textarea
          id="cfg-audience"
          className="wf-textarea"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          placeholder="audiência / persona"
        />
      </div>

      {/* Footer (wireframe .dl-f com padding 14px 0 0, sem borda) */}
      <div
        style={{
          padding: '14px 0 0',
          display: 'flex',
          gap: 9,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {msg && (
          <p
            className="text-xs"
            style={{
              marginRight: 'auto',
              color: msg.includes('sucesso') ? 'var(--wf-success-text)' : 'var(--wf-danger-text)',
            }}
          >
            {msg}
          </p>
        )}
        {/* Exclusão (§4.3.4) — danger à ESQUERDA do Cancelar, mesma dialog do card. */}
        <WfButton variant="danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
          Excluir
        </WfButton>
        <WfButton variant="ghost" onClick={resetForm} disabled={saving}>
          Cancelar
        </WfButton>
        <WfButton variant="primary" onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Salvando…' : 'Salvar alterações'}
        </WfButton>
      </div>

      {/* Sucesso: dialog dá o toast; aqui sai da tela do negócio excluído. */}
      <BusinessDeleteDialog
        business={confirmingDelete ? business : null}
        onClose={() => setConfirmingDelete(false)}
        onDeleted={() => navigate('/businesses')}
      />
    </div>
  );
}
